import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { required } from '../../../util/validators';
import { storeNameAvailable, composeValidators } from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  CustomExtendedDataField,
  FieldDateOfBirth,
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import { InputPhoneNumber } from '../../../components';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => {
  const { intl } = props;

  const validateForm = values => {
    const errors = {};
    const { fname, lname, email } = values || {};
    const disallowedUsernameMessage = intl
      ? intl.formatMessage({ id: 'validators.disallowedUsernameKeywords' })
      : 'Usernames and names containing reserved words are not allowed.';
    const disallowedValidator = validators.noDisallowedUsernameKeywords(disallowedUsernameMessage);

    if (email) {
      const emailDisallowedErr = disallowedValidator(email);
      if (emailDisallowedErr) {
        errors.email = emailDisallowedErr;
      }
    }

    if (fname || lname) {
      const fullNameWithSpace = `${fname || ''} ${lname || ''}`.trim();
      const fullNameNoSpace = `${fname || ''}${lname || ''}`.trim();
      const nameDisallowedErr =
        disallowedValidator(fullNameWithSpace) || disallowedValidator(fullNameNoSpace);
      if (nameDisallowedErr) {
        errors.fname = nameDisallowedErr;
        errors.lname = nameDisallowedErr;
      }
    }

    return errors;
  };

  return (
    <FinalForm
      {...props}
      validate={validateForm}
      mutators={{ ...arrayMutators }}
      initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          form,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
        } = formRenderProps;

        const { userType } = values || {};

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );
        const emailDisallowed = validators.noDisallowedUsernameKeywords(
          intl.formatMessage({ id: 'validators.disallowedUsernameKeywords' })
        );

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );
        const birthdayRequiredMessage = intl.formatMessage({
          id: 'SignupForm.birthdayRequired',
        });

        const validateBirthday = value => {
          if (!value) {
            return birthdayRequiredMessage;
          }

          // Parse the date string "YYYY-MM-DD"
          const dateParts = value.split('-');
          if (dateParts.length !== 3) {
            return intl.formatMessage({
              id: 'SignupForm.birthdayInvalid',
              defaultMessage: 'Please enter a valid date',
            });
          }

          const year = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10);
          const day = parseInt(dateParts[2], 10);

          // Check if date components are valid numbers
          if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return intl.formatMessage({
              id: 'SignupForm.birthdayInvalid',
              defaultMessage: 'Please enter a valid date',
            });
          }

          // Validate month range
          if (month < 1 || month > 12) {
            return intl.formatMessage({
              id: 'SignupForm.birthdayInvalidMonth',
              defaultMessage: 'Please enter a valid month',
            });
          }

          // Check leap year for February
          const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

          // Days in each month
          const daysInMonth = {
            1: 31,
            2: isLeapYear ? 29 : 28,
            3: 31,
            4: 30,
            5: 31,
            6: 30,
            7: 31,
            8: 31,
            9: 30,
            10: 31,
            11: 30,
            12: 31,
          };

          const maxDays = daysInMonth[month];

          // Validate day range for the specific month
          if (day < 1 || day > maxDays) {
            if (month === 2 && day > 28) {
              return intl.formatMessage({
                id: 'SignupForm.birthdayInvalidFebruary',
                defaultMessage: isLeapYear
                  ? 'February can only have up to 29 days in a leap year'
                  : 'February can only have up to 28 days',
              });
            }
            return intl.formatMessage(
              {
                id: 'SignupForm.birthdayInvalidDay',
                defaultMessage: 'This month can only have up to {maxDays} days',
              },
              { maxDays }
            );
          }

          // Additional check: verify date is not in the future
          const birthDate = new Date(year, month - 1, day);
          const today = new Date();

          if (birthDate > today) {
            return intl.formatMessage({
              id: 'SignupForm.birthdayFuture',
              defaultMessage: 'Birth date cannot be in the future',
            });
          }

          return undefined; // valid date
        };

        // Custom user fields. Since user types are not supported here,
        // only fields with no user type id limitation are selected.
        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
            />

            {showDefaultUserFields ? (
              <div className={css.defaultUserFields}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  validate={validators.composeValidators(emailRequired, emailValid, emailDisallowed)}
                />
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    validate={validators.composeValidators(
                      validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.firstNameRequired',
                        })
                      ),
                      validators.onlyAlphabetic(
                        intl.formatMessage({
                          id: 'validators.onlyAlphabetic',
                        })
                      ),
                      validators.noDisallowedUsernameKeywords(
                        intl.formatMessage({ id: 'validators.disallowedUsernameKeywords' })
                      )
                    )}
                    parse={validators.parseAlphabetic}
                  />

                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    validate={validators.composeValidators(
                      validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.lastNameRequired',
                        })
                      ),
                      validators.onlyAlphabetic(
                        intl.formatMessage({
                          id: 'validators.onlyAlphabetic',
                        })
                      ),
                      validators.noDisallowedUsernameKeywords(
                        intl.formatMessage({ id: 'validators.disallowedUsernameKeywords' })
                      )
                    )}
                    parse={validators.parseAlphabetic}
                  />
                </div>

                {userType === 'seller' ? (
                  <FieldTextInput
                    className={css.storeName}
                    type="text"
                    id={formId ? `${formId}.profileUrl` : 'profileUrl'}
                    name="profileUrl"
                    label={intl.formatMessage({
                      id: 'SignupForm.storeNameLabel',
                      defaultMessage: 'Store Name',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.storeNamePlaceholder',
                      defaultMessage: 'Enter your store name',
                    })}
                    validate={validators.composeValidators(
                      validators.required(
                        intl.formatMessage({
                          id: 'SignupForm.storeNameRequired',
                          defaultMessage: 'Store name is required',
                        })
                      ),
                      validators.noDisallowedUsernameKeywords(
                        intl.formatMessage({ id: 'validators.disallowedUsernameKeywords' })
                      ),
                      validators.storeNameAvailable
                    )}
                  />
                ) : null}

                <UserFieldDisplayName
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />
                <FieldDateOfBirth
                  id={formId ? `${formId}.birthday` : 'birthday'}
                  className={css.row}
                  name="birthday"
                  label={intl.formatMessage({
                    id: 'SignupForm.birthdayLabel',
                  })}
                  placeholder={{
                    day: 'DD',
                    month: 'MM',
                    year: 'YYYY',
                  }}
                  validate={validateBirthday}
                  showErrorMessage
                  rootClassName={css.dateOfBirth}
                />

                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({
                    id: 'SignupForm.passwordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.passwordPlaceholder',
                  })}
                  validate={passwordValidators}
                />

                {/* <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                form={form}
                intl={intl}
              /> */}
                <InputPhoneNumber
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  form={form}
                  intl={intl}
                  // prevent duplicate label
                  required={true}
                  placeholder="712345678"
                />
              </div>
            ) : null}

            {showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="SignupForm.signUp" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
